import React from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Select, Space, Tag, Row, Col, DatePicker, Statistic, Divider, Timeline, Empty, Tabs, Checkbox } from 'antd';
import { DollarOutlined, EditOutlined, DeleteOutlined, PlusOutlined, HistoryOutlined, FileExcelOutlined, DownloadOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useIsMobile } from '../hooks/useIsMobile';

dayjs.extend(quarterOfYear);

interface SalaryComponent {
  baseAmount: number;
  bonusAnswers: number;
  bonusClosedTickets: number;
  penaltyDelayed: number;
  ratingScore: number; // Changed from bonusRating to ratingScore (0-5)
  totalAdjustments: number;
}

interface SalaryAdjustment {
  id: number;
  type: 'bonus' | 'penalty' | 'manual_correction';
  amount: number;
  reason: string;
  ticketIds: string[];
  createdAt: string;
  madeBy: string;
  notes: string;
}

interface TeamMemberSalary {
  id: number;
  fullName: string;
  username: string;
  components: SalaryComponent;
  adjustments: SalaryAdjustment[];
  status: 'pending' | 'approved' | 'paid';
}

const SalaryCalculation: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [salaries, setSalaries] = React.useState<TeamMemberSalary[]>([]);
  const [adjustmentModalVisible, setAdjustmentModalVisible] = React.useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = React.useState<TeamMemberSalary | null>(null);
  const [historyVisible, setHistoryVisible] = React.useState(false);
  const [auditModalVisible, setAuditModalVisible] = React.useState(false);
  const [form] = Form.useForm();

  // Период выбора как в Dashboard
  const [preset, setPreset] = React.useState('last_month');
  const [range, setRange] = React.useState({
    start: dayjs().subtract(1, 'month').startOf('month'),
    end: dayjs().subtract(1, 'month').endOf('month')
  });
  const [rememberDefault, setRememberDefault] = React.useState(false);

  // Инициализация периода из localStorage
  React.useEffect(() => {
    const remember = localStorage.getItem('salary_period_remember') === '1';
    setRememberDefault(remember);
    const savedPreset = localStorage.getItem('salary_period_preset') || 'last_month';
    const startStr = localStorage.getItem('salary_period_start');
    const endStr = localStorage.getItem('salary_period_end');
    if (remember && startStr && endStr) {
      setPreset(savedPreset);
      setRange({ start: dayjs(startStr), end: dayjs(endStr) });
    }
  }, []);

  const persistIfNeeded = (pkey: string, r: { start: dayjs.Dayjs; end: dayjs.Dayjs }) => {
    if (rememberDefault) {
      localStorage.setItem('salary_period_remember', '1');
      localStorage.setItem('salary_period_preset', pkey);
      localStorage.setItem('salary_period_start', r.start.toISOString());
      localStorage.setItem('salary_period_end', r.end.toISOString());
    } else {
      localStorage.removeItem('salary_period_remember');
      localStorage.removeItem('salary_period_preset');
      localStorage.removeItem('salary_period_start');
      localStorage.removeItem('salary_period_end');
    }
  };

  const handlePresetChange = (value: string) => {
    setPreset(value);
    let newRange = { start: range.start, end: range.end };
    
    switch (value) {
      case 'this_month':
        newRange = { start: dayjs().startOf('month'), end: dayjs().endOf('month') };
        break;
      case 'last_month':
        newRange = { start: dayjs().subtract(1, 'month').startOf('month'), end: dayjs().subtract(1, 'month').endOf('month') };
        break;
      case 'this_quarter':
        newRange = { start: dayjs().startOf('quarter'), end: dayjs().endOf('quarter') };
        break;
      case 'last_quarter':
        newRange = { start: dayjs().subtract(1, 'quarter').startOf('quarter'), end: dayjs().subtract(1, 'quarter').endOf('quarter') };
        break;
      case 'this_year':
        newRange = { start: dayjs().startOf('year'), end: dayjs().endOf('year') };
        break;
      case 'last_year':
        newRange = { start: dayjs().subtract(1, 'year').startOf('year'), end: dayjs().subtract(1, 'year').endOf('year') };
        break;
    }
    
    setRange(newRange);
    persistIfNeeded(value, newRange);
  };

  // Вычисляем selectedMonth из range для обратной совместимости
  const selectedMonth = range.start;

  const isLikelyTestSalaryPayload = (payload: any): boolean => {
    if (!Array.isArray(payload)) return false;
    return payload.some((m: any) => {
      const username = String(m?.username || '').toLowerCase();
      const fullName = String(m?.fullName || '').toLowerCase();
      return (
        username.includes('support_user') ||
        username.includes('support_admin') ||
        fullName.includes('иван иванов') ||
        fullName.includes('мария петрова') ||
        fullName.includes('петр сидоров')
      );
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem(`salary_${selectedMonth.format('YYYY-MM')}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Ранее тут сохранялись тестовые мок-данные. Чистим их, чтобы в релизе не “висели”.
        if (isLikelyTestSalaryPayload(parsed)) {
          localStorage.removeItem(`salary_${selectedMonth.format('YYYY-MM')}`);
          setSalaries([]);
        } else {
          setSalaries(parsed);
        }
      } else {
        // В релизной версии не подставляем тестовые данные.
        // Расчет зарплат должен строиться на реальных данных / интеграции.
        setSalaries([]);
      }
    } catch (e: any) {
      message.error('Ошибка загрузки расчетов');
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, [range.start, range.end]);

  const getTotalSalary = (member: TeamMemberSalary): number => {
    const c = member.components;
    // Правильный расчет корректировок: бонусы добавляются, штрафы вычитаются
    const adjustmentsTotal = member.adjustments.reduce((sum, adj) => {
      if (adj.type === 'penalty') {
        return sum - Math.abs(adj.amount); // Штрафы вычитаются
      }
      return sum + adj.amount; // Бонусы и корректировки добавляются
    }, 0);
    
    // Рейтинг не выражается в рублях, только как скор
    // Если рейтинг > 4.5, добавляем премию в 10000
    const ratingBonus = c.ratingScore > 4.5 ? 10000 : 0;
    
    const total = c.baseAmount + c.bonusAnswers + c.bonusClosedTickets + c.penaltyDelayed + ratingBonus + adjustmentsTotal;
    return Math.max(0, total);
  };

  const handleAddAdjustment = (member: TeamMemberSalary) => {
    setSelectedTeamMember(member);
    form.resetFields();
    setAdjustmentModalVisible(true);
  };

  const handleSaveAdjustment = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedTeamMember) return;

      const newAdjustment: SalaryAdjustment = {
        id: Math.max(...selectedTeamMember.adjustments.map(a => a.id), 0) + 1,
        type: values.type,
        amount: parseFloat(values.amount),
        reason: values.reason,
        ticketIds: values.ticketIds ? values.ticketIds.split(',').map((t: string) => t.trim()) : [],
        createdAt: new Date().toISOString(),
        madeBy: 'admin',
        notes: values.notes || ''
      };

      const updated = salaries.map(s =>
        s.id === selectedTeamMember.id
          ? { ...s, adjustments: [...s.adjustments, newAdjustment] }
          : s
      );

      setSalaries(updated);
      localStorage.setItem(`salary_${selectedMonth.format('YYYY-MM')}`, JSON.stringify(updated));
      
      // Система уведомлений
      sendNotification(selectedTeamMember, newAdjustment);
      
      message.success('Корректировка добавлена и отправлена сотруднику');
      setAdjustmentModalVisible(false);
      setSelectedTeamMember(null);
    } catch (e: any) {
      message.error('Ошибка сохранения');
    }
  };

  const sendNotification = (teamMember: TeamMemberSalary, adjustment: SalaryAdjustment) => {
    // Симуляция отправки уведомлений
    const notificationData = {
      employeeName: teamMember.fullName,
      employeeEmail: `${teamMember.username}@company.com`,
      type: adjustment.type,
      amount: adjustment.amount,
      reason: adjustment.reason,
      tickets: adjustment.ticketIds,
      timestamp: new Date().toLocaleString('ru'),
      message: `${adjustment.type === 'penalty' ? 'Начислен штраф' : adjustment.type === 'bonus' ? 'Начислен бонус' : 'Произведена корректировка'}: ${adjustment.amount > 0 ? '+' : ''}${adjustment.amount}₽`
    };

    // Сохраняем уведомление в истории
    const notifications = JSON.parse(localStorage.getItem('salary_notifications') || '[]');
    notifications.push({
      ...notificationData,
      id: Date.now(),
      read: false
    });
    localStorage.setItem('salary_notifications', JSON.stringify(notifications));

    // Логирование (в реальном приложении отправить на backend)
    console.log('📬 PUSH NOTIFICATION SENT:', notificationData.message);
    console.log('📧 EMAIL SENT TO:', notificationData.employeeEmail);
    console.log('📝 REASON:', notificationData.reason);
    if (notificationData.tickets.length > 0) {
      console.log('🎫 TICKETS:', notificationData.tickets.join(', '));
    }
  };

  const handleDeleteAdjustment = (member: TeamMemberSalary, adjustmentId: number) => {
    Modal.confirm({
      title: 'Удалить корректировку?',
      content: 'Это действие нельзя отменить',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: () => {
        const updated = salaries.map(s =>
          s.id === member.id
            ? { ...s, adjustments: s.adjustments.filter(a => a.id !== adjustmentId) }
            : s
        );
        setSalaries(updated);
        localStorage.setItem(`salary_${selectedMonth.format('YYYY-MM')}`, JSON.stringify(updated));
        message.success('Корректировка удалена');
      }
    });
  };

  const columns = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 150,
      render: (name: string) => (
        <span style={{ fontSize: '13px', fontWeight: 500 }}>{name}</span>
      )
    },
    {
      title: 'Оклад',
      key: 'base',
      width: 100,
      render: (_: any, record: TeamMemberSalary) => (
        <span style={{ fontSize: '13px' }}>50 000 ₽</span>
      )
    },
    {
      title: 'Ответы',
      key: 'answers',
      width: 100,
      render: (_: any, record: TeamMemberSalary) => (
        <span style={{ fontSize: '13px', color: '#52c41a' }}>+{record.components.bonusAnswers} ₽</span>
      )
    },
    {
      title: 'Закрыто',
      key: 'closed',
      width: 100,
      render: (_: any, record: TeamMemberSalary) => (
        <span style={{ fontSize: '13px', color: '#52c41a' }}>+{record.components.bonusClosedTickets} ₽</span>
      )
    },
    {
      title: 'Штраф',
      key: 'penalty',
      width: 120,
      render: (_: any, record: TeamMemberSalary) => {
        // Расчет общей суммы штрафов: базовый штраф + штрафы из корректировок
        const basePenalty = record.components.penaltyDelayed;
        const adjustmentsPenalties = record.adjustments
          .filter(adj => adj.type === 'penalty')
          .reduce((sum, adj) => sum + Math.abs(adj.amount), 0);
        const totalPenalty = basePenalty - adjustmentsPenalties; // Вычитаем т.к. штрафы отрицательные
        
        return (
          <div>
            <span style={{ fontSize: '13px', color: '#ff4d4f', fontWeight: 600 }}>
              {totalPenalty} ₽
            </span>
            {adjustmentsPenalties > 0 && (
              <div style={{ fontSize: '11px', color: '#ff9500', marginTop: '2px' }}>
                ({basePenalty} + {adjustmentsPenalties})
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'Рейтинг',
      key: 'rating',
      width: 120,
      render: (_: any, record: TeamMemberSalary) => {
        const score = record.components.ratingScore;
        const hasBonus = score > 4.5;
        return (
          <div>
            <span style={{ fontSize: '13px', fontWeight: 600, color: score > 3 ? '#52c41a' : '#ff9500' }}>
              {score.toFixed(1)}/5
            </span>
            {hasBonus && (
              <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '2px' }}>
                +10000₽ бонус
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'Итого',
      key: 'total',
      width: 130,
      render: (_: any, record: TeamMemberSalary) => (
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1890ff' }}>
          {getTotalSalary(record).toLocaleString()} ₽
        </span>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const colors: { [key: string]: string } = { pending: 'orange', approved: 'blue', paid: 'green' };
        const labels: { [key: string]: string } = { pending: 'Ожидает', approved: 'Одобрено', paid: 'Выплачено' };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      }
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: TeamMemberSalary) => (
        <Space size="small" wrap>
          <Button
            size="small"
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={() => handleAddAdjustment(record)}
            style={{ fontSize: '12px' }}
          >
            Корректировка
          </Button>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => {
              setSelectedTeamMember(record);
              setHistoryVisible(true);
            }}
            style={{ fontSize: '12px' }}
          >
            История
          </Button>
        </Space>
      )
    }
  ];

  return (
    <>
    
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space wrap>
            <Select
              value={preset}
              onChange={handlePresetChange}
              options={[
                { value: 'this_month', label: 'Этот месяц' },
                { value: 'last_month', label: 'Прошлый месяц' },
                { value: 'this_quarter', label: 'Этот квартал' },
                { value: 'last_quarter', label: 'Прошлый квартал' },
                { value: 'this_year', label: 'Этот год' },
                { value: 'last_year', label: 'Прошлый год' },
                { value: 'custom', label: 'Произвольно' },
              ]}
              style={{ width: 200 }}
            />
            <DatePicker.RangePicker
              value={[range.start, range.end]}
              format="DD.MM.YYYY"
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) {
                  const r = { start: vals[0], end: vals[1] };
                  setRange(r);
                  setPreset('custom');
                  persistIfNeeded('custom', r);
                }
              }}
              allowClear={false}
            />
            <Checkbox 
              checked={rememberDefault} 
              onChange={(e) => {
                setRememberDefault(e.target.checked);
                persistIfNeeded(preset, range);
              }}
            >
              По умолчанию
            </Checkbox>
          </Space>
        </Col>
      </Row>

      <Card
        title="Расчет заработной платы"
        loading={loading}
        extra={
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={() => message.info('Экспорт в Excel - скоро')}>
              Экспорт
            </Button>
            <Button icon={<HistoryOutlined />} onClick={() => setAuditModalVisible(true)}>
              Аудит
            </Button>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Всего сотрудников"
              value={salaries.length}
              prefix={<DollarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Общий ФОТ"
              value={salaries.reduce((sum, s) => sum + getTotalSalary(s), 0)}
              suffix="₽"
              precision={0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Ожидает одобрения"
              value={salaries.filter(s => s.status === 'pending').length}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Выплачено"
              value={salaries.filter(s => s.status === 'paid').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={salaries}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Нет данных' }}
          scroll={isMobile ? { x: 'max-content' } : undefined}
          size={isMobile ? 'small' : 'middle'}
        />

        {/* Модаль корректировки */}
        <Modal
          title={`Корректировка: ${selectedTeamMember?.fullName}`}
          open={adjustmentModalVisible}
          onCancel={() => {
            setAdjustmentModalVisible(false);
            setSelectedTeamMember(null);
            form.resetFields();
          }}
          onOk={handleSaveAdjustment}
          okText="Добавить"
          width={isMobile ? '100%' : 600}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Тип"
              name="type"
              rules={[{ required: true }]}
            >
              <Select
                style={{ width: '100%' }}
                options={[
                  { label: 'Бонус', value: 'bonus' },
                  { label: 'Штраф', value: 'penalty' },
                  { label: 'Корректировка', value: 'manual_correction' }
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Сумма"
              name="amount"
              rules={[{ required: true, message: 'Введите сумму' }]}
            >
              <Input type="number" placeholder="0" size="large" addonAfter="₽" />
            </Form.Item>
            <Form.Item
              label="Причина"
              name="reason"
              rules={[{ required: true, message: 'Введите причину' }]}
            >
              <Input.TextArea rows={2} placeholder="Причина корректировки" />
            </Form.Item>
            <Form.Item
              label="Номера обращений (через запятую)"
              name="ticketIds"
            >
              <Input placeholder="#1234, #1235" />
            </Form.Item>
            <Form.Item
              label="Комментарий"
              name="notes"
            >
              <Input.TextArea rows={2} placeholder="Дополнительные заметки" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Модаль истории */}
        <Modal
          title={`История корректировок: ${selectedTeamMember?.fullName}`}
          open={historyVisible}
          onCancel={() => {
            setHistoryVisible(false);
            setSelectedTeamMember(null);
          }}
          footer={null}
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          {selectedTeamMember && selectedTeamMember.adjustments.length > 0 ? (
            <Timeline
              items={selectedTeamMember.adjustments.map(adj => ({
                label: new Date(adj.createdAt).toLocaleDateString('ru'),
                children: (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                      <Tag color={adj.type === 'penalty' ? 'red' : 'green'}>
                        {adj.type === 'bonus' ? 'Бонус' : adj.type === 'penalty' ? 'Штраф' : 'Корректировка'}
                      </Tag>
                      {adj.type === 'penalty' ? '-' : '+'}{adj.amount} ₽
                    </div>
                    <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                      <strong>Причина:</strong> {adj.reason}
                    </div>
                    {adj.ticketIds.length > 0 && (
                      <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                        <strong>Обращения:</strong> {adj.ticketIds.map((ticket, idx) => (
                          <React.Fragment key={idx}>
                            <a 
                              href={`/support-admin#${ticket}`} 
                              style={{ color: '#1890ff', cursor: 'pointer', marginLeft: '4px' }}
                            >
                              {ticket}
                            </a>
                            {idx < adj.ticketIds.length - 1 && ', '}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    {adj.notes && (
                      <div style={{ fontSize: '13px', marginBottom: '4px', color: '#666' }}>
                        <strong>Заметки:</strong> {adj.notes}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                      Добавил: {adj.madeBy}
                    </div>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteAdjustment(selectedTeamMember, adj.id)}
                      style={{ marginTop: '8px' }}
                    >
                      Удалить
                    </Button>
                  </div>
                )
              }))}
            />
          ) : (
            <Empty description="Нет корректировок" />
          )}
        </Modal>

        {/* Модаль аудита - полный функционал */}
        <Modal
          title="Аудит зарплаты"
          open={auditModalVisible}
          onCancel={() => setAuditModalVisible(false)}
          footer={null}
          width={isMobile ? '100%' : 1200}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          <AuditContent selectedMonth={selectedMonth} />
        </Modal>
      </Card>
    </>
  );
};

// Компонент аудита (весь функционал из SalaryAudit)
const AuditContent: React.FC<{ selectedMonth: dayjs.Dayjs }> = ({ selectedMonth }) => {
  const [auditLogs, setAuditLogs] = React.useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = React.useState<string | undefined>(undefined);

  const load = () => {
    const notifications = JSON.parse(localStorage.getItem('salary_notifications') || '[]');
    setAuditLogs(notifications.map((n: any) => ({
      id: n.id,
      timestamp: n.timestamp,
      employeeName: n.employeeName,
      employeeEmail: n.employeeEmail,
      type: n.type,
      amount: n.amount,
      reason: n.reason,
      tickets: n.tickets || [],
      madeBy: n.madeBy || 'admin',
      notes: n.notes || ''
    })));
  };

  React.useEffect(() => { load(); }, []);

  const filteredLogs = auditLogs.filter(log => {
    const logMonth = new Date(log.timestamp).toLocaleString('ru', { month: 'long', year: 'numeric' });
    const currentMonth = selectedMonth.format('MMMM YYYY');
    if (logMonth !== currentMonth) return false;
    if (selectedEmployee && log.employeeName !== selectedEmployee) return false;
    return true;
  });

  const employees = Array.from(new Set(auditLogs.map(log => log.employeeName)));

  const handleExport = () => {
    let csvContent = 'Дата,Сотрудник,Email,Тип,Сумма,Причина,Обращения\n';
    filteredLogs.forEach(log => {
      csvContent += `${log.timestamp},${log.employeeName},${log.employeeEmail},${log.type},${log.amount},${log.reason},${log.tickets.join(' ')}\n`;
    });
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    element.setAttribute('download', `audit_${selectedMonth.format('YYYY-MM')}.csv`);
    element.click();
  };

  const totalBonuses = filteredLogs.filter(l => l.type === 'bonus').reduce((sum, l) => sum + l.amount, 0);
  const totalPenalties = filteredLogs.filter(l => l.type === 'penalty').reduce((sum, l) => sum + Math.abs(l.amount), 0);

  return (
    <>
    <div>
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={8}>
          <Statistic title="Всего записей" value={filteredLogs.length} valueStyle={{ color: '#1890ff' }} />
        </Col>
        <Col span={8}>
          <Statistic title="Бонусы" value={totalBonuses} suffix="₽" valueStyle={{ color: '#52c41a' }} />
        </Col>
        <Col span={8}>
          <Statistic title="Штрафы" value={totalPenalties} suffix="₽" valueStyle={{ color: '#ff4d4f' }} />
        </Col>
      </Row>

      <Space style={{ marginBottom: '16px' }}>
        <Select
          placeholder="Фильтр по сотруднику"
          allowClear
          value={selectedEmployee}
          onChange={setSelectedEmployee}
          style={{ width: 200 }}
          options={employees.map(emp => ({ label: emp, value: emp }))}
        />
        <Button icon={<DownloadOutlined />} onClick={handleExport}>Экспорт CSV</Button>
      </Space>

      {filteredLogs.length > 0 ? (
        <Table
          rowKey="id"
          dataSource={filteredLogs}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Дата', dataIndex: 'timestamp', width: 180, render: (t: string) => new Date(t).toLocaleString('ru') },
            { title: 'Сотрудник', dataIndex: 'employeeName', width: 150 },
            { title: 'Тип', dataIndex: 'type', width: 120, render: (type: string) => {
              const colors: any = { bonus: 'green', penalty: 'red', manual_correction: 'blue' };
              const labels: any = { bonus: 'Бонус', penalty: 'Штраф', manual_correction: 'Корректировка' };
              return <Tag color={colors[type]}>{labels[type]}</Tag>;
            }},
            { title: 'Сумма', dataIndex: 'amount', width: 100, render: (amt: number, rec: any) => (
              <span style={{ fontWeight: 600, color: rec.type === 'penalty' ? '#ff4d4f' : '#52c41a' }}>
                {rec.type === 'penalty' ? '-' : '+'}{amt}₽
              </span>
            )},
            { title: 'Причина', dataIndex: 'reason', ellipsis: true },
            { title: 'Обращения', dataIndex: 'tickets', width: 150, render: (tickets: string[]) => (
              tickets.length > 0 ? tickets.map(t => <Tag key={t}>{t}</Tag>) : '-'
            )},
          ]}
        />
      ) : (
        <Empty description="Нет записей за выбранный период" />
      )}
    </div>
    </>
  );
};

export default SalaryCalculation;
